import React, { FC } from 'react';
import { VideoIframeProps, VideoOptimizerProps } from './type';
import { useIsMobile } from 'hooks/useIsMobile';
import { PrismicImageWithAlt } from '../PrismicImageWithAlt';
import classNames from 'classnames';

export const VideoOptimizer: FC<VideoOptimizerProps> = ({
  videoRef,
  videoUrl,
  poster,
  alt = 'Video content',
  autoplay = false,
  loop = true,
  muted = true,
  controls,
  playsInline = true,
  maxHeight,
  aspectRatio,
  objectFit = 'cover',
  contrast = 100,
  brightness = 100,
  onPlay,
  onPause,
  preload = 'auto',
  imageClass,
  videoClass,
  ariaLabel,
  enableKeyboardControls = true,
  onPlayPause,
}) => {
  const { isMobile } = useIsMobile(639);

  // Keep original autoplay behavior
  const shouldAutoplay = autoplay;

  // Simple keyboard handler
  const handleKeyDown = (event: React.KeyboardEvent<HTMLVideoElement>) => {
    if (!enableKeyboardControls) return;

    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault();
      onPlayPause?.();
    }
  };

  // If there's no video, show poster
  if (!videoUrl) {
    return (
      <PrismicImageWithAlt
        className={imageClass}
        defaultAlt={alt}
        field={poster}
        priority
        showPlaceholderIfEmpty
        sizes="100vw"
        style={{
          objectFit: maxHeight ? 'cover' : 'contain',
          objectPosition: 'center',
          aspectRatio: !maxHeight ? aspectRatio : undefined,
          width: '100%',
          maxHeight,
        }}
      />
    );
  }

  return (
    <video
      aria-label={ariaLabel || alt}
      autoPlay={shouldAutoplay}
      className={classNames(videoClass)}
      controls={controls === 'Detailed'}
      loop={loop}
      muted={muted}
      onKeyDown={handleKeyDown}
      onPause={onPause}
      onPlay={onPlay}
      playsInline={playsInline}
      poster={poster?.url!}
      preload={isMobile ? 'none' : preload}
      ref={videoRef}
      style={{
        maxHeight,
        aspectRatio,
        objectFit,
        filter: `contrast(${contrast}%) brightness(${brightness}%)`,
      }}
      tabIndex={enableKeyboardControls ? 0 : undefined}
    >
      <source src={videoUrl} />
    </video>
  );
};

export const VideoIframe: FC<VideoIframeProps> = ({
  videoUrl,
  poster,
  alt = 'Video content',
  autoplay = false,
  maxHeight,
  aspectRatio,
  title = 'Video content',
  className,
  imageClass,
  style,
}) => {
  const ytVideoSrc = autoplay
    ? `${videoUrl}?autoplay=${autoplay}&mute=1`
    : videoUrl;

  // If there's no video, show poster
  if (!videoUrl) {
    return (
      <PrismicImageWithAlt
        className={imageClass}
        defaultAlt={alt}
        field={poster}
        priority
        showPlaceholderIfEmpty
        sizes="100vw"
        style={{
          objectFit: maxHeight ? 'cover' : 'contain',
          objectPosition: 'center',
          aspectRatio: !maxHeight ? aspectRatio : undefined,
          width: '100%',
          maxHeight,
        }}
      />
    );
  }

  return (
    <iframe
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
      allowFullScreen
      aria-label={title}
      className={className}
      role="application"
      src={ytVideoSrc}
      style={{
        maxHeight,
        aspectRatio,
        ...style,
      }}
      title={title}
    />
  );
};
